import React, { useEffect, useMemo, useState } from "react";
import api from "../../services/apiClient.ts";
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';
import PaymentEditModal from '../payments/PaymentEditModal';
import ErrorBoundary from '../common/ErrorBoundary';

/**
 * PaymentsPage.jsx — نسخة محدثة
 * - سلكتات بالأسماء من السيرفر (agents / supervisors / generals) عبر /payments/meta
 * - إعداد axios صحيح عبر VITE_API_BASE_URL أو القيمة الافتراضية http://localhost:8000/api/v1
 * - Interceptor يحقن Authorization + X-Tenant + X-Branch (+ X-Vault اختيارياً)
 */

// استخدام عميل API الموحد القادم من services/apiClient

// ➤ أدوات مساعدة — إرسال التاريخ فقط بدون وقت (YYYY-MM-DD 00:00:00)
function toBackendDateOnly(localValue) {
  if (!localValue) return "";
  const dt = new Date(localValue);
  if (isNaN(dt.getTime())) return localValue;
  const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
  const y = dt.getFullYear();
  const m = pad(dt.getMonth() + 1);
  const d = pad(dt.getDate());
  return `${y}-${m}-${d} 00:00:00`;
}

function formatAmount(row) {
  // اختيار المبلغ غير الصفري: الدفع يستخدم amount_debit، الاستلام يستخدم amount_credit
  const debit = Number(row.amount_debit || 0);
  const credit = Number(row.amount_credit || 0);
  const val = debit > 0 ? debit : credit;
  return `${val.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} د.ع`;
}

// تنسيق المبلغ بفواصل الآلاف للعرض
function formatWithCommas(val) {
  if (val === "" || val === null || val === undefined) return "";
  const str = String(val).replace(/,/g, "");
  const num = parseFloat(str);
  if (isNaN(num)) return str;
  const parts = num.toFixed(2).split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts[1] === "00" ? parts[0] : parts.join(".");
}

function parseFormattedNumber(str) {
  if (!str || str === "") return "";
  const cleaned = String(str).replace(/,/g, "");
  const num = parseFloat(cleaned);
  return isNaN(num) ? "" : num;
}

// اسم المرجع: وكيل أو مشرف أو عام من الميتا (عمود واحد يجمع الثلاثة)
function getRefName(row, meta) {
  if (!row || !meta) return "-";
  if (row.paytype === "Agent" && row.ag_id) {
    const a = (meta.agents || []).find((x) => Number(x.id) === Number(row.ag_id));
    return a?.name ?? row.ag_id;
  }
  if (row.paytype === "Supervisor" && row.su_id) {
    const s = (meta.supervisors || []).find((x) => Number(x.id) === Number(row.su_id));
    return s?.name ?? row.su_id;
  }
  if (row.paytype === "General" && row.ge_id) {
    const g = (meta.generals || []).find((x) => Number(x.id) === Number(row.ge_id));
    return g?.name ?? g?.title ?? row.ge_id;
  }
  return "-";
}

export default function PaymentsPage() {
  const { t, i18n } = useTranslation();
  const isArabic = i18n?.language?.startsWith('ar');
  const creationDisabled = false;
  const [meta, setMeta] = useState({
    actiontypes: ["Pay", "Receive"],
    paytypes: ["Agent", "Supervisor", "General"],
    agents: [],
    supervisors: [],
    generals: [],
  });

  const [filters, setFilters] = useState({
    paytype: "",
    actiontype: "",
    ref_id: "", // وكيل أو مشرف أو عام حسب paytype — فارغ = الكل
    vault: "",
    from: "",
    to: "",
    per_page: 10,
  });

  const [form, setForm] = useState({
    actiontype: "Pay",
    paytype: "Agent",
    ag_id: "",
    su_id: "",
    ge_id: "",
    amount: "",
    notes: "",
    tx_date_local: "",
  });

  const [list, setList] = useState({ data: [], current_page: 1, last_page: 1, total: 0 });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Modals state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editTargetId, setEditTargetId] = useState(null);

  // ➤ تحميل الميتا (ثوابت + القوائم)
  useEffect(() => {
    let mounted = true;
    api
      .get("/v1/payments/meta")
      .then(({ data }) => {
        if (!mounted) return;
        setMeta((prev) => ({
          ...prev,
          actiontypes: data.actiontypes || prev.actiontypes,
          paytypes: data.paytypes || prev.paytypes,
          agents: data.agents || [],
          supervisors: data.supervisors || [],
          generals: data.generals || [],
        }));
      })
      .catch((e) => {
        console.warn("Failed to load meta:", e?.response?.data || e.message);
      });
    return () => (mounted = false);
  }, []);

  // ➤ جلب القائمة — ref_id يتحول إلى ag_id / su_id / ge_id حسب paytype؛ إن كان فارغاً لا يُرسل (الكل)
  const fetchPayments = async (page = 1) => {
    setLoading(true);
    setError("");
    try {
      const { ref_id, ...rest } = filters;
      const params = { ...rest, page };
      if (ref_id && filters.paytype === "Agent") params.ag_id = ref_id;
      if (ref_id && filters.paytype === "Supervisor") params.su_id = ref_id;
      if (ref_id && filters.paytype === "General") params.ge_id = ref_id;
      const { data } = await api.get("/v1/payments", { params });
      setList(data);
    } catch (e) {
      setError(t('common.error'));
      toast.error(e?.response?.data?.message || e.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ➤ إرسال إنشاء سجل
  const onSubmit = async (e) => {
    e.preventDefault();
    if (creationDisabled) {
      toast.warn(t('payments.creationDisabled'));
      return;
    }
    // minimal validations
    const amountValue = Number(form.amount);
    if (!Number.isFinite(amountValue) || amountValue <= 0) {
      toast.error(t('payments.validation.totalAmountRequired'));
      return;
    }
    const refKey = form.paytype === 'Agent' ? 'ag_id' : form.paytype === 'Supervisor' ? 'su_id' : 'ge_id';
    if (!form[refKey]) {
      toast.error(t('travelers.validationRequired'));
      return;
    }
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        actiontype: form.actiontype,
        paytype: form.paytype,
        amount: amountValue,
        notes: form.notes || '',
        tx_date: toBackendDateOnly(form.tx_date_local),
        ag_id: form.ag_id || undefined,
        su_id: form.su_id || undefined,
        ge_id: form.ge_id || undefined,
      };
      await api.post('/v1/payments', payload);
      setSuccess(t('common.success'));
      toast.success(t('common.success'));
      fetchPayments(list.current_page || 1);
    } catch (e) {
      setError(t('common.error'));
      toast.error(e?.response?.data?.message || e.message || t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  // ➤ حذف سجل عبر مودال تأكيد
  const onDeleteRequest = (id) => {
    setDeleteTargetId(id);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTargetId) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/v1/payments/${deleteTargetId}`);
      setSuccess(t('common.success'));
      toast.success(t('common.success'));
      setDeleteModalOpen(false);
      setDeleteTargetId(null);
      fetchPayments(list.current_page || 1);
    } catch (e) {
      setError(t('common.error'));
      toast.error(e?.response?.data?.message || e.message || t('common.error'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDelete = () => {
    setDeleteModalOpen(false);
    setDeleteTargetId(null);
  };

  // ➤ تجميعة الحقول حسب نوع الدفع
  const refLabel = useMemo(() => {
    switch (form.paytype) {
      case "Agent":
        return { key: "ag_id", label: t('travelers.agentName') };
      case "Supervisor":
        return { key: "su_id", label: t('payments.paytypeSupervisor') };
      case "General":
      default:
        return { key: "ge_id", label: t('payments.generalExpenses') };
    }
  }, [form.paytype, t]);

  const setRefValue = (v) => {
    if (refLabel.key === "ag_id") setForm((p) => ({ ...p, ag_id: v, su_id: "", ge_id: "" }));
    if (refLabel.key === "su_id") setForm((p) => ({ ...p, su_id: v, ag_id: "", ge_id: "" }));
    if (refLabel.key === "ge_id") setForm((p) => ({ ...p, ge_id: v, ag_id: "", su_id: "" }));
  };

  // مصادر الخيارات حسب النوع
  const currentOptions = useMemo(() => {
    if (form.paytype === "Agent") return meta.agents || [];
    if (form.paytype === "Supervisor") return meta.supervisors || [];
    return meta.generals || [];
  }, [form.paytype, meta]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100" dir={isArabic ? 'rtl' : 'ltr'}>
      <div className="max-w-6xl mx-auto p-4 md:p-6">
        <h1 className="text-2xl md:text-3xl font-semibold mb-6">{t('navigation.PaymentsPage')}</h1>
        {/* status messages */}
        {(error || success) && (
          <div className="mb-4 space-y-2">
            {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-red-800">{error}</div>}
            {success && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-emerald-800">{success}</div>
            )}
          </div>
        )}
        {/* Form */}
        <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-12 gap-4 rounded-2xl border bg-white dark:bg-gray-800 p-4 shadow-sm dark:border-gray-700" aria-disabled={creationDisabled}>
          {/* actiontype */}
          <div className="md:col-span-2">
            <label className="block text-sm mb-1">{t('payments.actionType')}</label>
            <select
              className="w-full rounded-xl border px-3 py-2 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              value={form.actiontype}
              onChange={(e) => setForm((p) => ({ ...p, actiontype: e.target.value }))}
            >
              {meta.actiontypes?.map((atype) => (
                <option key={atype} value={atype}>{atype === "Pay" ? t('payments.actionPay') : t('payments.actionReceive')}</option>
              ))}
            </select>
          </div>

          {/* paytype */}
          <div className="md:col-span-2">
            <label className="block text-sm mb-1">{t('payments.paymentType')}</label>
            <select
              className="w-full rounded-xl border px-3 py-2 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              value={form.paytype}
              onChange={(e) => setForm((p) => ({ ...p, paytype: e.target.value }))}
            >
              {meta.paytypes?.map((pt) => (
                <option key={pt} value={pt}>{pt === "Agent" ? t('payments.paytypeAgent') : pt === "Supervisor" ? t('payments.paytypeSupervisor') : t('payments.generalExpenses')}</option>
              ))}
            </select>
          </div>

          {/* ref select */}
          <div className="md:col-span-3">
            <label className="block text-sm mb-1">{refLabel.label}</label>
            <select
              className="w-full rounded-xl border px-3 py-2 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              value={refLabel.key === "ag_id" ? form.ag_id : refLabel.key === "su_id" ? form.su_id : form.ge_id}
              onChange={(e) => setRefValue(e.target.value)}
            >
              <option value="">{t('common.select')}</option>
              {currentOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.name ?? opt.title ?? opt.label ?? opt.id}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">{t('payments.metaLoadedHint')}</p>
          </div>

          {/* amount */}
          <div className="md:col-span-2">
            <label className="block text-sm mb-1">{t('common.amount')}</label>
            <input
              type="text"
              inputMode="numeric"
              className="w-full rounded-xl border px-3 py-2 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              placeholder="0.00"
              value={formatWithCommas(form.amount)}
              onChange={(e) => {
                const parsed = parseFormattedNumber(e.target.value);
                setForm((p) => ({ ...p, amount: parsed === "" ? "" : parsed }));
              }}
              required
            />
          </div>

          {/* date */}
          <div className="md:col-span-3">
            <label className="block text-sm mb-1">{t('common.date')}</label>
            <input
              type="date"
              className="w-full rounded-xl border px-3 py-2 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              value={form.tx_date_local}
              onChange={(e) => setForm((p) => ({ ...p, tx_date_local: e.target.value }))}
            />
          </div>

          {/* notes */}
          <div className="md:col-span-12">
            <label className="block text-sm mb-1">{t('payments.notes')}</label>
            <textarea
              rows={2}
              className="w-full rounded-xl border px-3 py-2 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              placeholder={t('payments.enterNotes')}
              value={form.notes}
              onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
            />
          </div>

          <div className="md:col-span-12 flex items-center gap-3">
            <button type="submit" disabled={creationDisabled || submitting} className="rounded-xl bg-blue-600 px-5 py-2 text-white hover:bg-blue-700 disabled:opacity-60" title={creationDisabled ? t('common.warning') : t('common.submit')}>
              {creationDisabled ? t('common.save') : (submitting ? t('common.loading') : t('common.save'))}
            </button>
            <button type="button" onClick={() => fetchPayments(1)} className="rounded-xl border px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700">
              {t('common.refresh')}
            </button>
          </div>
        </form>
        {/* فلاتر القائمة */}
        <div className="mt-6 rounded-2xl border bg-white dark:bg-gray-800 p-4 shadow-sm dark:border-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm mb-1">{t('payments.actionType')}</label>
              <select className="w-full rounded-xl border px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100" value={filters.actiontype} onChange={(e) => setFilters((p) => ({ ...p, actiontype: e.target.value }))}>
                <option value="">{t('common.all')}</option>
                {meta.actiontypes?.map((atype) => (
                  <option key={atype} value={atype}>{atype === "Pay" ? t('payments.actionPay') : t('payments.actionReceive')}</option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm mb-1">{t('payments.paymentType')}</label>
              <select
                className="w-full rounded-xl border px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                value={filters.paytype}
                onChange={(e) => setFilters((p) => ({ ...p, paytype: e.target.value, ref_id: "" }))}
              >
                <option value="">{t('common.all')}</option>
                {meta.paytypes?.map((pt) => (
                  <option key={pt} value={pt}>{pt === "Agent" ? t('payments.paytypeAgent') : pt === "Supervisor" ? t('payments.paytypeSupervisor') : t('payments.generalExpenses')}</option>
                ))}
              </select>
            </div>

            {/* الاسم: قائمة وكيل أو مشرف أو عام حسب نوع الدفع — فارغ = الكل */}
            <div className="md:col-span-2">
              <label className="block text-sm mb-1">{t('common.name')}</label>
              <select
                className="w-full rounded-xl border px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                value={filters.ref_id}
                onChange={(e) => setFilters((p) => ({ ...p, ref_id: e.target.value }))}
                disabled={!filters.paytype}
              >
                <option value="">{t('common.all')}</option>
                {filters.paytype === "Agent" && (meta.agents || []).map((opt) => (
                  <option key={opt.id} value={opt.id}>{opt.name ?? opt.id}</option>
                ))}
                {filters.paytype === "Supervisor" && (meta.supervisors || []).map((opt) => (
                  <option key={opt.id} value={opt.id}>{opt.name ?? opt.id}</option>
                ))}
                {filters.paytype === "General" && (meta.generals || []).map((opt) => (
                  <option key={opt.id} value={opt.id}>{opt.name ?? opt.title ?? opt.id}</option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm mb-1">{t('common.fromDate')}</label>
              <input type="date" className="w-full rounded-xl border px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100" value={filters.from} onChange={(e) => setFilters((p) => ({ ...p, from: e.target.value }))} />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm mb-1">{t('common.toDate')}</label>
              <input type="date" className="w-full rounded-xl border px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100" value={filters.to} onChange={(e) => setFilters((p) => ({ ...p, to: e.target.value }))} />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm mb-1">{t('common.perPage')}</label>
              <select className="w-full rounded-xl border px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100" value={filters.per_page} onChange={(e) => setFilters((p) => ({ ...p, per_page: Number(e.target.value) }))}>
                {[10, 20, 50].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2 flex items-end">
              <button type="button" onClick={() => fetchPayments(1)} className="w-full rounded-xl bg-gray-800 px-4 py-2 text-white hover:bg-black">
                {t('common.submit')}
              </button>
            </div>
          </div>
        </div>

        {/* الجدول */}
        <div className="mt-4 app-table-wrap shadow-sm">
          <table className="app-table min-w-full text-sm">
            <thead className="bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-100">
              <tr>
                <th className="px-3 py-2 text-left">{t('common.index')}</th>
                <th className="px-3 py-2 text-left">{t('common.date')}</th>
                <th className="px-3 py-2 text-left">{t('payments.actionType')}</th>
                <th className="px-3 py-2 text-left">{t('payments.paymentType')}</th>
                <th className="px-3 py-2 text-left">{t('common.name')}</th>
                <th className="px-3 py-2 text-left">{t('common.amount')}</th>
                <th className="px-3 py-2 text-left">{t('common.notes')}</th>
                <th className="px-3 py-2 text-left">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center text-gray-500">{t('common.loading')}</td>
                </tr>
              ) : list.data?.length ? (
                list.data.map((row, idx) => (
                    <tr key={row.id} className="border-t hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-3 py-2">{(list.current_page - 1) * (list.per_page || 10) + idx + 1}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{row.created_at ? new Date(row.created_at).toLocaleDateString() : '-'}</td>
                      <td className="px-3 py-2">{row.actiontype === "Pay" ? t('payments.actionPay') : t('payments.actionReceive')}</td>
                      <td className="px-3 py-2">{row.paytype === "Agent" ? t('payments.paytypeAgent') : row.paytype === "Supervisor" ? t('payments.paytypeSupervisor') : t('payments.generalExpenses')}</td>
                      <td className="px-3 py-2">{getRefName(row, meta)}</td>
                      <td className="px-3 py-2">{formatAmount(row)}</td>
                      <td className="px-3 py-2 max-w-[300px] truncate" title={row.notes || ""}>{row.notes || ""}</td>
                      <td className="px-3 py-2 space-x-2 rtl:space-x-reverse">
                        <button className="rounded-lg border px-3 py-1 hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => { setEditTargetId(row.id); setEditModalOpen(true); }}>{t('common.edit')}</button>
                        <button className="rounded-lg bg-red-600 px-3 py-1 text-white hover:bg-red-700" onClick={() => onDeleteRequest(row.id)}>{t('common.delete')}</button>
                      </td>
                    </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center text-gray-500">{t('common.noResults')}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ترقيم الصفحات */}
        <div className="mt-3 flex items-center justify-between text-sm">
          <div className="text-gray-600">{t('common.total')}: {list.total || 0}</div>
          <div className="flex items-center gap-2">
            <button className="rounded-lg border px-3 py-1 disabled:opacity-50" disabled={(list.current_page || 1) <= 1} onClick={() => fetchPayments((list.current_page || 1) - 1)}>{t('common.previous')}</button>
            <span>{t('common.page')} {(list.current_page || 1)} / {(list.last_page || 1)}</span>
            <button className="rounded-lg border px-3 py-1 disabled:opacity-50" disabled={(list.current_page || 1) >= (list.last_page || 1)} onClick={() => fetchPayments((list.current_page || 1) + 1)}>{t('common.next')}</button>
          </div>
        </div>
      {/* Modals */}
      <ErrorBoundary fallback={null}>
        <ConfirmDeleteModal
          open={deleteModalOpen}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
          loading={deleteLoading}
          recordLabel={`${t('common.delete')} #${deleteTargetId ?? ''}`}
        />
        <PaymentEditModal
          open={editModalOpen}
          recordId={editTargetId}
          onClose={() => { setEditModalOpen(false); setEditTargetId(null); }}
          onSaved={() => fetchPayments(list.current_page || 1)}
        />
      </ErrorBoundary>
      </div>
    </div>
  );
}
